import { Auth0Error } from "auth0-js";
import * as Sentry from "@sentry/browser";

type PortalAuthAction = "login" | "signup" | "forgotPassword";

const DUPLICATE_SIGNUP_CODE = "user_exists";

/**
 * Mistyping a password is something people do all day, and reporting it would
 * bury the failures that need attention.
 *
 * `access_denied` is in here because that — not `invalid_user_password` — is
 * what a wrong password returns when credentials are posted from our own page.
 *
 * A duplicate signup is the same kind of ordinary mistake. Measured across the
 * portals it arrives at a steady low rate spread evenly over partners, with no
 * burst to read as an attack, and what the person needs is to be told to log
 * in — which is the signup form's job, not an alert's. It only reaches us as a
 * distinct code while the tenant discloses that an address is taken; under
 * Auth0's "hide user existence" setting the same refusal arrives as
 * `invalid_signup`, which stays reportable because it also covers signups being
 * disabled on the connection.
 *
 * `too_many_attempts` is the lockout Auth0's brute-force protection applies,
 * and this list used to keep it reportable on the grounds that a rise in
 * lockouts signals an attack. It does — but not anywhere this capture can see
 * it. Auth0 is what refuses, at Auth0's own endpoints, and credentials posted
 * straight at those never load our React form; what reaches here is the one
 * customer who mistyped often enough to be locked out and is being told to wait
 * it out. An actual attack is in the tenant's log stream instead, with the
 * source address and the account attached — both the only place it is visible
 * and the only place it is actionable. What Sentry got was an issue per
 * locked-out booker, none of them individually resolvable.
 *
 * Still absent are blocks and server errors: those are a tenant or connection
 * misconfigured, which is ours to fix, so they stay reportable.
 */
const ROUTINE_FAILURES = new Set([
  "invalid_user_password",
  "access_denied",
  DUPLICATE_SIGNUP_CODE,
  "too_many_attempts",
]);

function authErrorCode(error: unknown): string {
  if (typeof error !== "object" || error === null) return "";
  // `||`, not `??`: auth0-js occasionally sets an empty-string `code`, and we
  // want to fall through to `error` in that case rather than key off "".
  const { code, error: errorField } = error as Partial<Auth0Error>;
  return code || errorField || "";
}

export function isRoutineAuthFailure(error: unknown): boolean {
  return ROUTINE_FAILURES.has(authErrorCode(error));
}

/**
 * Lets the signup form point a returning customer at the login page instead of
 * showing them a generic failure. Takes `unknown` because that is what a
 * rejected promise hands a `catch` block: auth0-js rejects with a plain object,
 * a transport failure with a real Error carrying neither code field.
 */
export function isDuplicateSignup(error: unknown): boolean {
  return authErrorCode(error) === DUPLICATE_SIGNUP_CODE;
}

/**
 * Whether the request never completed, as opposed to completing in a refusal.
 * The network dropped, the page was unloading, or a client blocked the tenant's
 * domain — auth0-js raises one error for all of them and flags the original
 * with `crossDomain`.
 *
 * Worth its own test because such a failure carries no code to be routine or
 * reportable by: there was no response to read one off, so auth0-js repackages
 * it with a null `code` and a null `description`, and the report built below
 * reads "failed: unknown" — no code, no status, and not one frame of ours on
 * the stack. It is the same browser-side blip `processSentryEvent` already
 * drops by message when `fetch` throws it, arriving through a library that
 * words it differently.
 *
 * Read off `original` rather than the message: the message is superagent's own
 * prose, free to be reworded, and the repackaging drops it anyway.
 */
function isTransportFailure(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;

  const { original } = error as { original?: { crossDomain?: unknown } };

  return original?.crossDomain === true;
}

// auth0-js hands back a plain object, which Sentry can only group under a
// generic "Object captured as exception" title. Wrap it in a real Error keyed
// by action + code so genuine failures group meaningfully.
function toReportableError(error: Auth0Error, action: PortalAuthAction): Error {
  if (error instanceof Error) return error;
  return new Error(
    `Portal ${action} failed: ${authErrorCode(error) || "unknown"}`
  );
}

export function reportPortalAuthError(
  error: Auth0Error,
  action: PortalAuthAction
): void {
  console.error(`${action} error:`, error);
  if (isRoutineAuthFailure(error) || isTransportFailure(error)) return;
  Sentry.captureException(toReportableError(error, action), {
    // Sentry's default server-side scrubber matches the substring "auth" in a
    // key name and files the value as "[Filtered]", so this key has to avoid
    // that substring or the code is lost for triage. A unit test cannot catch
    // it — the test reads the call arguments, before Sentry ever sees them.
    extra: { providerCode: authErrorCode(error), action },
  });
}
